import cn from 'classnames'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { TPopupHorizontal, TPopupVertical } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'
import { PLAN_SECTION_DECK_ATTRIBUTE } from 'partner360/lib/planSectionDialog'
import { ConfirmDialog } from 'shared/components/confirmDialog'
import { NetworkDebugDialog } from 'shared/components/networkDebugDialog'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { useCockpitRecalc } from '../../model/hooks/useCockpitRecalc'
import { selectCockpitAccessLevel } from '../../model/selectors'
import { COCKPIT_DRAWER_HOST_SELECTOR, CockpitDrawer } from '../components/cockpitDrawer'

import { HelpPanelContent } from './components/helpPanelContent'
import { formatRecalcDate } from './lib/formatRecalcDate'
import { BellIcon, PanelIcon, ReadyIcon } from './lib/icons'
import type { ICockpitHelpRecalc, ICockpitHelpTour, TCockpitHelpMode } from './types'

import styles from './CockpitHelpTools.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.helpPanel

interface ICockpitHelpToolsProps {
  /** The partner the panel's actions and offerings belong to; the manager cockpit has none. */
  partnerId?: string
  mode: TCockpitHelpMode
  tour?: ICockpitHelpTour
  tourId?: string
  /** The trigger as the card header's framed icon chip, its label in the tooltip, for a row with
   *  no room for the word. */
  compact?: boolean
  /** The trigger as a framed chip with the word Menu before the mark, for the partner cockpit's
   *  top row, where it stands beside the sponsor chip at its height. */
  menu?: boolean
}

/** "Help & tools": opens the side panel holding the page's help, its actions, the partner's
 *  offerings and the switch between the new and the classic cockpit. In the cockpit it is a
 *  segment of the tray's toolbar; on the classic page it is drawn exactly like the JBP Training
 *  Centre button it stands beside. */
export const CockpitHelpTools = ({
  partnerId,
  mode,
  tour,
  tourId,
  compact = false,
  menu = false,
}: ICockpitHelpToolsProps) => {
  const t = useSafetyTranslation()
  const anchorRef = useRef<HTMLSpanElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isNetworkDataOpen, setIsNetworkDataOpen] = useState(false)
  const open = useCallback(() => setIsOpen(true), [])
  const openNetworkData = useCallback(() => setIsNetworkDataOpen(true), [])
  const closeNetworkData = useCallback(() => setIsNetworkDataOpen(false), [])

  /* Recalculating the stored cockpit is the WorldWide user's tool. The store holds the manager
     cockpit — the user's whole book of partners — so the row belongs to that cockpit only. */
  const accessLevel = useSelector(selectCockpitAccessLevel)
  const isWorldWide = accessLevel?.trim().toLowerCase() === 'worldwide'
  const recalc = useCockpitRecalc(mode === 'manager' && isWorldWide)
  const [isRecalcConfirmOpen, setIsRecalcConfirmOpen] = useState(false)
  const recalcRow = useMemo<ICockpitHelpRecalc | undefined>(
    () =>
      recalc.isAvailable
        ? {
            isRunning: recalc.isRunning,
            requestedAt: recalc.requestedAt,
            readyAt: recalc.readyAt,
            onRequest: () => setIsRecalcConfirmOpen(true),
            onRefresh: recalc.refresh,
          }
        : undefined,
    [recalc.isAvailable, recalc.isRunning, recalc.readyAt, recalc.refresh, recalc.requestedAt]
  )
  const confirmRecalc = useCallback(() => {
    setIsRecalcConfirmOpen(false)
    void recalc.request()
  }, [recalc])

  /* The bell and its hover list stand only while the recalculated data waits to be loaded. */
  const readyAt = recalcRow?.readyAt ?? null

  const close = useCallback(() => {
    setIsOpen(false)
    anchorRef.current?.querySelector('button')?.focus()
  }, [])

  return (
    <>
      <span ref={anchorRef} className={styles.anchor} data-tour-id={tourId}>
        {mode === 'classic' ? (
          <Button className={styles.classicButton} onClick={open}>
            <Flex alignItems={'center'}>
              <PanelIcon className={styles.classicIcon} />
              <Translation text={KEYS.trigger} />
            </Flex>
          </Button>
        ) : (
          <Tooltip
            inline
            disabled={!readyAt && !compact && !menu}
            textAlign="left"
            /* Under the trigger, opening leftwards from its right edge: the arrow sits at that
               edge, under the mark the label is followed by. */
            verticalDirection={TPopupVertical.Bottom}
            horizontalDirection={TPopupHorizontal.Left}
            tipContent={() =>
              readyAt ? (
                <div className={styles.notifications}>
                  <span className={styles.notificationsCaption}>
                    <Translation text={KEYS.notifications} span />
                  </span>
                  <span className={styles.notification}>
                    <ReadyIcon className={styles.notificationIcon} />
                    <span>
                      <span className={styles.notificationTitle}>
                        <Translation text={KEYS.recalcReady} span />
                      </span>
                      <span className={styles.notificationText}>
                        <Translation
                          text={KEYS.recalcReadyAt}
                          replaceParams={[
                            { key: '{date}', value: formatRecalcDate(readyAt ?? '') },
                          ]}
                          span
                        />
                      </span>
                      <span className={styles.notificationText}>
                        <Translation text={KEYS.recalcReadyHint} span />
                      </span>
                    </span>
                  </span>
                </div>
              ) : (
                <span>
                  <Translation text={KEYS.trigger} span />
                </span>
              )
            }
          >
            <button
              type="button"
              className={cn(styles.trigger, {
                [styles.compactTrigger]: compact,
                [styles.menuTrigger]: menu,
              })}
              aria-haspopup="dialog"
              aria-expanded={isOpen}
              aria-label={
                readyAt
                  ? `${t(KEYS.trigger)}: ${t(KEYS.recalcReady)}`
                  : compact || menu
                    ? t(KEYS.trigger)
                    : undefined
              }
              onClick={open}
            >
              {/* The mark follows the label in both states, so swapping it for the bell leaves the
              label where it was. */}
              {!compact && <Translation text={menu ? KEYS.menu : KEYS.trigger} span />}
              {readyAt ? (
                <BellIcon className={cn(styles.icon, styles.bell)} />
              ) : (
                <PanelIcon className={styles.icon} />
              )}
            </button>
          </Tooltip>
        )}
      </span>

      <CockpitDrawer
        isOpen={isOpen}
        onClose={close}
        getHost={() => anchorRef.current?.closest<HTMLElement>(COCKPIT_DRAWER_HOST_SELECTOR)}
        /* Opened from the plan deck over a plan section, it opens over that section too. */
        container={
          isOpen
            ? anchorRef.current?.closest<HTMLElement>(`[${PLAN_SECTION_DECK_ATTRIBUTE}]`)
            : undefined
        }
        title={<Translation text={KEYS.title} span />}
        ariaLabel={t(KEYS.title)}
        closeLabel={t(KEYS.close)}
      >
        <HelpPanelContent
          partnerId={partnerId}
          mode={mode}
          tour={tour}
          onClose={close}
          onOpenNetworkData={openNetworkData}
          recalc={recalcRow}
        />
      </CockpitDrawer>

      {isNetworkDataOpen && <NetworkDebugDialog show onClose={closeNetworkData} />}

      <ConfirmDialog
        isVisible={isRecalcConfirmOpen}
        size="xs"
        title={KEYS.recalcConfirmTitle}
        content={<Translation text={KEYS.recalcConfirmText} span />}
        confirmBtnText={KEYS.recalcConfirm}
        rejectBtnText="partnercommitment-cancel"
        onConfirm={confirmRecalc}
        onReject={() => setIsRecalcConfirmOpen(false)}
      />
    </>
  )
}

CockpitHelpTools.displayName = 'CockpitHelpTools'
